// components/features/tournaments/components/SmartAnalysisCard.jsx
import React from 'react';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Clock, 
  TrendingUp,
  AlertCircle,
  Info,
  Zap
} from '../../../common/Icons';
import { COMPARISON_TYPES, CONFLICT_SEVERITY } from '../../../../utils/comparisonEngine';

const SmartAnalysisCard = ({ analysis }) => {
  if (!analysis) {
    return (
      <div className="p-3 bg-[#191c23] rounded-lg border border-white/[0.06]">
        <div className="text-center text-gray-400">
          <Info className="w-5 h-5 mx-auto mb-2" />
          <p className="text-sm">Loading analysis...</p>
        </div>
      </div>
    );
  }

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case CONFLICT_SEVERITY.NONE:
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case CONFLICT_SEVERITY.LOW:
        return <Info className="w-5 h-5 text-[#23F8B5]" />;
      case CONFLICT_SEVERITY.MEDIUM:
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case CONFLICT_SEVERITY.HIGH:
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case CONFLICT_SEVERITY.CRITICAL:
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case CONFLICT_SEVERITY.NONE:
        return 'border-green-500/40 bg-green-900/20';
      case CONFLICT_SEVERITY.LOW:
        return 'border-[#31C590]/40 bg-[#31C590]/10';
      case CONFLICT_SEVERITY.MEDIUM:
        return 'border-yellow-500/40 bg-yellow-900/20';
      case CONFLICT_SEVERITY.HIGH:
        return 'border-orange-500/40 bg-orange-900/20';
      case CONFLICT_SEVERITY.CRITICAL:
        return 'border-red-500/40 bg-red-900/20';
      default:
        return 'border-white/10 bg-[#22262f]';
    }
  };

  const getSeverityTextColor = (severity) => {
    switch (severity) {
      case CONFLICT_SEVERITY.NONE:
        return 'text-green-400';
      case CONFLICT_SEVERITY.LOW:
        return 'text-[#23F8B5]';
      case CONFLICT_SEVERITY.MEDIUM:
        return 'text-yellow-300';
      case CONFLICT_SEVERITY.HIGH:
        return 'text-orange-300';
      case CONFLICT_SEVERITY.CRITICAL:
        return 'text-red-300';
      default:
        return 'text-gray-300';
    }
  };

  const getComparisonTypeTitle = (type) => {
    switch (type) {
      case COMPARISON_TYPES.PERFECT_MATCH:
        return 'Perfect Match';
      case COMPARISON_TYPES.SCORE_MATCH_STATS_DIFFER:
        return 'Scores Match, Stats Differ';
      case COMPARISON_TYPES.LINEUP_MISMATCH:
        return 'Lineup Mismatch';
      case COMPARISON_TYPES.STATS_TOTAL_MISMATCH:
        return 'Statistical Inconsistency';
      case COMPARISON_TYPES.MVP_CONFLICT:
        return 'MVP Conflict';
      case COMPARISON_TYPES.EVIDENCE_MISMATCH:
        return 'Evidence Mismatch';
      case COMPARISON_TYPES.COMPLETELY_DIFFERENT:
        return 'Major Discrepancies';
      case COMPARISON_TYPES.PARTIAL_SUBMISSION:
        return 'Partial Submission';
      default:
        return 'Analysis Complete';
    }
  };

  const renderQuickStats = () => {
    const stats = [];

    if (analysis.scores) {
      stats.push({
        label: 'Scores',
        status: analysis.scores.status,
        icon: analysis.scores.status === 'match' ? CheckCircle : XCircle
      });
    }

    if (analysis.playerStats) {
      const matchingPlayers = analysis.playerStats.summary?.matchingPlayers || 0;
      const totalPlayers = analysis.playerStats.summary?.totalPlayers || 0;
      stats.push({
        label: 'Player Stats',
        status: analysis.playerStats.status,
        detail: `${matchingPlayers}/${totalPlayers} match`,
        icon: analysis.playerStats.status === 'match' ? CheckCircle : XCircle
      });
    }

    if (analysis.lineups) {
      stats.push({
        label: 'Lineups',
        status: analysis.lineups.status,
        detail: analysis.lineups.playersMatch ? 'Same players' : 'Different players',
        icon: analysis.lineups.status === 'match' ? CheckCircle : XCircle
      });
    }

    if (analysis.mvp) {
      stats.push({
        label: 'MVP',
        status: analysis.mvp.status,
        detail: analysis.mvp.match ? 'Same selection' : 'Different selection',
        icon: analysis.mvp.status === 'match' ? CheckCircle : XCircle
      });
    }

    return stats;
  };

  const quickStats = renderQuickStats();

  return (
    <div className={`rounded-xl border p-4 ${getSeverityColor(analysis.severity)}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2">
          {getSeverityIcon(analysis.severity)}
          <div>
            <h3 className={`text-base font-bold ${getSeverityTextColor(analysis.severity)}`}>
              {getComparisonTypeTitle(analysis.comparisonType)}
            </h3>
            <p className={`text-xs ${getSeverityTextColor(analysis.severity)} opacity-90`}>
              {analysis.conflictSummary}
            </p>
          </div>
        </div>

        {/* Auto-approvable indicator */}
        {analysis.autoApprovable && (
          <div className="flex items-center space-x-2 px-3 py-1 bg-green-900/30 border border-green-500/40 rounded-full">
            <Zap className="w-4 h-4 text-green-400" />
            <span className="text-xs font-medium text-green-300">
              Auto-approvable
            </span>
          </div>
        )}
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
        {quickStats.map((stat, index) => (
          <div
            key={index}
            className="flex items-center space-x-2 p-3 bg-[#22262f] rounded-lg border border-white/[0.07]"
          >
            <stat.icon className={`w-4 h-4 ${
              stat.status === 'match'
                ? 'text-green-400'
                : stat.status === 'differ'
                  ? 'text-red-500'
                  : 'text-gray-500'
            }`} />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-gray-300">
                {stat.label}
              </p>
              {stat.detail && (
                <p className="text-xs text-gray-400 truncate">
                  {stat.detail}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Recommendations */}
      {analysis.recommendations && analysis.recommendations.length > 0 && (
        <div className="space-y-2">
          <h4 className={`text-sm font-semibold ${getSeverityTextColor(analysis.severity)} mb-2`}>
            Smart Recommendations:
          </h4>
          <div className="space-y-1">
            {analysis.recommendations.slice(0, 3).map((recommendation, index) => (
              <div key={index} className="flex items-start space-x-2">
                <TrendingUp className={`w-3 h-3 mt-0.5 flex-shrink-0 ${getSeverityTextColor(analysis.severity)} opacity-70`} />
                <p className={`text-xs ${getSeverityTextColor(analysis.severity)} opacity-90`}>
                  {recommendation}
                </p>
              </div>
            ))}
          </div>
          
          {analysis.recommendations.length > 3 && (
            <p className={`text-xs ${getSeverityTextColor(analysis.severity)} opacity-70 mt-2`}>
              +{analysis.recommendations.length - 3} more recommendations...
            </p>
          )}
        </div>
      )}

      {/* Special handling for partial submissions */}
      {analysis.comparisonType === COMPARISON_TYPES.PARTIAL_SUBMISSION && analysis.partialSubmissionInfo && (
        <div className="mt-2 p-3 bg-[#22262f] border border-white/[0.07] rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Clock className="w-4 h-4 text-[#23F8B5]" />
            <span className="text-sm font-medium text-[#23F8B5]">
              Waiting for {analysis.partialSubmissionInfo.missingTeam === 'team1' ? 'Team 1' : 'Team 2'}
            </span>
          </div>
          <p className="text-xs text-gray-400">
            You can approve the available submission or wait for both teams to submit.
          </p>
        </div>
      )}

      {/* Statistical validation warnings */}
      {analysis.statistical && !analysis.statistical.bothValid && (
        <div className="mt-2 p-3 bg-red-900/30 border border-red-500/40 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium text-red-300">
              Statistical Issues Detected
            </span>
          </div>
          <div className="space-y-1">
            {!analysis.statistical.team1Valid && (
              <p className="text-xs text-red-400">
                Team 1: {analysis.statistical.team1Validation.reason}
              </p>
            )}
            {!analysis.statistical.team2Valid && (
              <p className="text-xs text-red-400">
                Team 2: {analysis.statistical.team2Validation.reason}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartAnalysisCard;